"use client";

import { createContext, useContext, useEffect, useRef, type ReactNode } from "react";
import { SITE } from "@/lib/constants";
import { RESIZE_MESSAGE } from "@/lib/embed";
import { useT } from "@/components/i18n/I18nProvider";

const EmbedContext = createContext<string | null>(null);
export const useEmbed = () => useContext(EmbedContext);

export function EmbedFrame({ path, name, children }: { path: string; name: string; children: ReactNode }) {
  const { t } = useT();
  const root = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const element = root.current!;
    // No inbound message handler: the host cannot supply scores, URLs or code.
    // Only public layout dimensions leave the frame. Referrer may be withheld.
    let origin = "*";
    try {
      const referrer = new URL(document.referrer);
      if (referrer.protocol === "https:" || referrer.protocol === "http:") origin = referrer.origin;
    } catch { /* optional */ }
    let lastHeight = 0;
    const resize = () => {
      const height = Math.max(200, Math.ceil(element.getBoundingClientRect().height));
      if (height !== lastHeight && window.parent !== window) {
        lastHeight = height;
        window.parent.postMessage({ type: RESIZE_MESSAGE, version: 1, height }, origin);
      }
    };
    const observer = new ResizeObserver(resize);
    observer.observe(element);
    resize();
    return () => observer.disconnect();
  }, []);
  return (
    <EmbedContext.Provider value={path}>
      <div ref={root} className="embed-frame mx-auto max-w-3xl p-3 sm:p-5">
        <div className="mb-3 flex items-center justify-between gap-3 text-sm">
          <span className="shrink-0 whitespace-nowrap font-bold tracking-tight text-foreground">Helibrain<span className="text-primary" aria-hidden> ·</span></span>
          <span className="text-end text-xs text-muted-foreground">{name}</span>
        </div>
        <main>{children}</main>
        <p className="pt-4 text-center text-xs text-muted-foreground">
          <a href={SITE.url} target="_blank" rel="noopener" className="rounded px-2 py-1 underline underline-offset-4">{t("embed.powered")}</a>
        </p>
      </div>
    </EmbedContext.Provider>
  );
}
